import { useTranslate } from "ra-core";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";

import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";
import { useIsMobile } from "@/hooks/use-mobile";

import "@/styles/market-intel.css";

import { CompetitorShow } from "./CompetitorShow";
import { CompetitorsView } from "./CompetitorsView";
import { DailyView } from "./DailyView";
import { IdeasView } from "./IdeasView";
import { OpportunitiesView } from "./OpportunitiesView";
import {
  MARKET_BASE,
  MARKET_TABS,
  prospectsPathOf,
  tabOfPath,
  type MarketTab,
} from "./routes";
import { WeeklyView } from "./WeeklyView";

/**
 * "Inteligencia de mercado": Flavia's daily reports, weekly digests,
 * competitors, prospects and the ideas board, under /inteligencia/*. On a phone the
 * section switcher is a sticky glass segmented control under the top bar.
 */
export const MarketIntelPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = tabOfPath(pathname);

  const options = MARKET_TABS.map((entry) => ({
    value: entry.id,
    label: translate(`market.tabs.${entry.id}`),
  }));
  const onChange = (next: MarketTab) =>
    navigate(`${MARKET_BASE}/${MARKET_TABS.find((t) => t.id === next)!.path}`);

  return (
    // overflow-x-clip is only a safety net (it keeps sticky working, unlike
    // hidden): the cards themselves are built to never exceed the width.
    <div
      className={
        isMobile ? "overflow-x-clip px-4" : "mx-auto w-full max-w-[1180px] pt-2"
      }
    >
      <header
        className={
          isMobile
            ? "pb-2"
            : "mb-5 flex flex-wrap items-end justify-between gap-4"
        }
      >
        <div className="min-w-0">
          <h1
            className={
              isMobile
                ? "text-2xl leading-tight font-bold tracking-tight"
                : "text-2xl font-semibold tracking-tight"
            }
          >
            {translate("market.title")}
          </h1>
        </div>
        {isMobile ? null : (
          <SegmentedControl
            value={tab}
            options={options}
            onChange={onChange}
            label={translate("market.tabs.label")}
            fit
            className="w-[38rem]"
          />
        )}
      </header>
      {isMobile ? (
        <div className="mi-tabs-sticky">
          <SegmentedControl
            value={tab}
            options={options}
            onChange={onChange}
            label={translate("market.tabs.label")}
            glass
            size="sm"
            fit
          />
        </div>
      ) : null}
      <div className={isMobile ? "pt-1" : undefined}>
        <Routes>
          <Route index element={<Navigate to="diario" replace />} />
          <Route path="diario" element={<DailyView />} />
          <Route path="semanal" element={<WeeklyView />} />
          <Route path="competidores" element={<CompetitorsView />} />
          <Route path="competidores/:id" element={<CompetitorShow />} />
          <Route path="prospectos" element={<OpportunitiesView />} />
          <Route
            path="oportunidades/*"
            element={<LegacyOpportunitiesRedirect />}
          />
          <Route path="ideas" element={<IdeasView />} />
          <Route path="*" element={<Navigate to="diario" replace />} />
        </Routes>
      </div>
    </div>
  );
};

MarketIntelPage.path = `${MARKET_BASE}/*`;

/** Old /inteligencia/oportunidades links: same subpath, query and hash. */
const LegacyOpportunitiesRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      replace
      to={{ pathname: prospectsPathOf(pathname), search, hash }}
    />
  );
};
